<template>
    <transition-group name="fade" tag="ul" class="list-group list-tricks">
        <li
            v-for="(trick, index) in validatedTricks"
            :key="index"
            :data-id="trick.combo_id"
            class="mb-1"
            :draggable="!isReadOnly && reorderMode"
            @dragstart="onDragStart(index, $event)"
            @dragover.prevent="onDragOver(index, $event)"
            @drop.prevent="onDrop(index)"
            :class="{ 'reorder-active': reorderMode }"
        >
            <template v-if="trick.hasOwnProperty('header') && trick.hasOwnProperty('body')">
                <div
                    :id="'combo-' + trick.header.combo_id"
                    class="container position-relative"
                    :ref="`draggableContainer-${index}`"
                    @mousedown="isView ? null : startDragging(index)"
                    @mousemove="isView ? null : (isDragging && handleDragging())"
                    @mouseup="isView ? null : stopDragging(index)"
                    @touchstart="isView ? null : startDragging(index)"
                    @touchmove="isView ? null : (isDragging && handleDragging())"
                    @touchend="isView ? null : stopDragging(index)"
                    @click.stop="toggleCollapse(trick.header.combo_id)"
                >
                    <div
                        class="row position-relative rounded-pill bg-white"
                        :style="{ transform: `translateX(${index === draggingIndex ? containerOffsetX : 0}px)` }"
                        style="z-index: 2; width: auto;"
                    >
                        <div class="col-1 bg-primary d-flex align-items-center bg-secondary bg-gradient bg-opacity-25  rounded-start-pill ms-0 me-0">
                            <div class="level-circle-combo d-flex align-content-center justify-content-center">
                                <b class="text-sm-general">
                                    {{ trick.header.combo_id }}
                                </b>
                            </div>
                        </div>
                        <div class="col-8 position-relative bg-secondary bg-gradient bg-opacity-25 rounded-0 ms-0 me-0">
                            <div class="row">
                                <div class="col-12 text-start p-1 pb-0 mb-0">
                                    <p class="mb-0 text-sm-title" >
                                        <strong>Combo</strong>
                                    </p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 text-start p-1 pt-0 mt-0">
                                    <p class="m-0 p-0 text-secondary text-sm-general">
                                        {{ $t('judging_program.validated_tricks_box.tricks_in_combo') }}:
                                        <b class="total-tricks-combo text-black">{{ trick.header.total_tricks_combo }}</b>
                                    </p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <div class="d-flex justify-content-center align-items-center gap-2 rounded-pill border border-secondary-subtle text-center bg-secondary"
                                         :class="{'bg-opacity-50': !collapsedStates[trick.header.combo_id], 'bg-opacity-20': collapsedStates[trick.header.combo_id]}">
                                        <i :class="{'fas fa-arrow-down': !collapsedStates[trick.header.combo_id], 'fas fa-arrow-up text-white': collapsedStates[trick.header.combo_id]}"></i>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="comboFeedbackState(trick)"
                                class="combo-feedback-icon position-absolute top-50 end-0 translate-middle-y me-2"
                                :class="comboFeedbackState(trick) === 'ok' ? 'combo-feedback-icon--ok' : 'combo-feedback-icon--bad'"
                                :title="comboFeedbackState(trick) === 'ok' ? 'Todos confirmados' : 'Hay feedback negativo en el combo'"
                            >
                                <i :class="comboFeedbackState(trick) === 'ok' ? 'fas fa-thumbs-up' : 'fas fa-thumbs-down'"></i>
                            </div>
                        </div>
                        <div class="col-1 p-0 bg-secondary bg-gradient bg-opacity-25  rounded-0 ms-0 me-0">
                            <div class="flex-column h-100">
                            </div>
                        </div>
                        <div class="col-2 d-flex justify-content-center align-items-center bg-success bg-gradient bg-opacity-50  rounded-end-pill ms-0 me-0">
                            <div class="text-center text-sm-score">
                                <b :id="'total_score_combo_'+trick.header.combo_id">{{ parseFloat(trick.header.total_score.toFixed(2)) }}</b>
                            </div>
                        </div>
                    </div>
                    <!-- Segundo row con botones -->
                    <div class="row position-absolute top-0" style="width: 100%; height: 100%;">
                        <div class="col-6 d-flex align-items-center justify-content-start bg-primary bg-opacity-50 rounded-start-pill ms-0 me-0" :class="{ 'overlay-disabled': reorderMode }">
                            <i class="fas fa-pencil-alt text-white"></i>
                            <span class="text-white mx-2">
                                <em>
                                    {{ $t('judging_program.validated_tricks_box.edit') }}
                                </em>
                        </span>
                        </div>
                        <div class="col-6 d-flex align-items-center justify-content-end bg-danger bg-opacity-50 rounded-end-pill ms-0 me-0" :class="{ 'overlay-disabled': reorderMode }">
                            <span class="text-white mx-2"><em> {{ $t('judging_program.validated_tricks_box.delete') }}</em></span><i class="fas fa-trash text-white"></i>
                        </div>
                    </div>
                </div>
                <template v-if="collapsedStates[trick.header.combo_id]">
                    <div class="combo-body-group">
                        <div
                            v-for="(subTrick, subIndex) in trick.body"
                            :key="subIndex"
                            class="container my-1 px-0"
                            style="transition: height 0.2s ease-out;"
                        >
                            <div class="combo-body">
                                <div
                                    class="container position-relative"
                                    :ref="`draggableContainer-${index+'-'+subIndex}`"
                                    @mousedown="isView ? null : startDragging(index,subIndex)"
                                    @mousemove="isView ? null : (isDragging && handleDragging())"
                                    @mouseup="isView ? null : stopDragging(index,subIndex)"
                                    @touchstart="isView ? null : startDragging(index,subIndex)"
                                    @touchmove="isView ? null : (isDragging && handleDragging())"
                                    @touchend="isView ? null : stopDragging(index,subIndex)"
                                >
                                    <div
                                        class="row position-relative rounded-pill bg-white"
                                        :style="{ transform: `translateX(${index+'-'+subIndex === draggingIndex ? containerOffsetX : 0}px)` }"
                                        style="z-index: 2; width: auto;"
                                    >
                                        <div class="col-1 bg-primary d-flex align-items-center bg-secondary bg-gradient bg-opacity-25 rounded-start-pill ms-0 me-0">
                                            <div class="level-circle" :class="getBackgroundColorClass(subTrick.level)">
                                                <b class="text-white text-sm-general">{{ subTrick.level }}</b>
                                            </div>
                                        </div>
                                        <div class="col-8 position-relative bg-secondary bg-gradient bg-opacity-25 rounded-0 ms-0 me-0">
                                            <div class="row">
                                                <div class="col-12 text-start p-1 pb-0 mb-0 text-truncate">
                                                    <p class="mb-0 text-sm-title d-flex align-items-center gap-1">
                                                        <strong>{{ subTrick.name }}</strong>
                                                        <span
                                                            v-if="badgeResolver(subTrick, subIndex)"
                                                            class="badge badge-change"
                                                            :class="badgeResolver(subTrick, subIndex).variantClass"
                                                            :title="badgeResolver(subTrick, subIndex).tooltip"
                                                            @click.stop="handleBadgeClick(subTrick, subIndex)"
                                                        >
                                                            {{ badgeResolver(subTrick, subIndex).label }}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div class="col-12 text-start p-1 pt-0 mt-0">
                                                    <p class="m-0 p-0 text-secondary text-sm-general">{{ subTrick.short_name ? subTrick.short_name : '&nbsp;' }}</p>
                                                </div>
                                            </div>
                                            <div v-if="isStatsExcluded(subTrick)" class="row">
                                                <div class="col-12 text-start p-1 pt-0 mt-0">
                                                    <p class="m-0 stats-excluded-note" :title="statsExcludedTooltip(subTrick)">
                                                        <i class="fas fa-triangle-exclamation"></i>
                                                        <span>{{ statsExcludedLabel(subTrick) }}</span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div class="col-6 p-0">
                                                    <div class="rounded-pill border border-secondary-subtle text-center combo-pill" style="background-color: rgb(37,102,199,0.3)">
                                                        <p class="m-0 px-1 text-sm-alerts"><em>{{ getCategoryName(subTrick.category) }}</em></p>
                                                    </div>
                                                </div>
                                                <div class="col-6 p-0">
                                                    <div v-if="subTrick.grab" class="rounded-pill border border-secondary-subtle text-center combo-pill" style="background-color: rgba(250,230,41,0.3)">
                                                        <p class="m-0 px-1 text-sm-alerts"><em>{{ getGrabName(subTrick.grab) }}</em></p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                v-if="isPublishedRound && subTrick.athlete_feedback"
                                                class="position-absolute top-50 end-0 translate-middle-y me-2 d-flex flex-column align-items-end"
                                            >
                                                <i
                                                    class="fas"
                                                    :class="{
      'fa-thumbs-up text-success': subTrick.athlete_feedback === 1,
      'fa-thumbs-down text-danger': subTrick.athlete_feedback === 2
    }"
                                                    style="font-size: 1.2rem;"
                                                ></i>

                                                <small
                                                    v-if="isPublishedRound && subTrick.athlete_feedback === 2 && subTrick.athlete_comment"
                                                    class="text-danger text-end mt-1 bg-white bg-opacity-75 px-2 py-1 rounded"
                                                    style="max-width: 200px;"
                                                >
                                                    <em>{{ subTrick.athlete_comment }}</em>
                                                </small>
                                            </div>
                                        </div>
                                        <div class="col-1 p-0 bg-secondary bg-gradient bg-opacity-25 rounded-0 ms-0 me-0">
                                            <div class="flex-column h-100">
                                                <div v-if="subTrick.variation" class="alert alert-secondary m-0 p-0 h-50">
                                                    <p class="m-0 text-sm-title">
                                                        <strong class="position-absolute top-0 start-50 translate-middle-x" style="line-height: 1.6rem">
                                                            {{ subTrick.variation === 1 ? 'FS' : (subTrick.variation === 2 ? 'BS' : '') }}
                                                        </strong>
                                                    </p>
                                                </div>
                                                <div v-if="subTrick.dub" class="alert alert-danger m-0 p-0 h-50" style="background-color: rgba(208,0,0,0.7)">
                                                    <p class="m-0 text-sm-title">
                                                        <strong class="text-white position-absolute top-0 start-50 translate-middle-x">
                                                            {{ subTrick.dub === 1 ? '20%' : (subTrick.dub === 2 ? '50%' : (subTrick.dub === 3 ? '80%' : '0%')) }}
                                                        </strong>
                                                        <i class="fa-solid fa-angle-down text-white position-absolute bottom-0 start-50 translate-middle-x"></i>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-2 d-flex justify-content-center align-items-center bg-success bg-gradient bg-opacity-50 rounded-end-pill ms-0 me-0">
                                            <div class="text-center text-sm-score">
                                                <strong class="p-1">{{ parseFloat(subTrick.score.toFixed(2)) }}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row position-absolute top-0" style="width: 100%; height: 100%;">
                                        <div class="col-6 d-flex align-items-center justify-content-start bg-primary bg-opacity-50 rounded-start-pill ms-0 me-0" :class="{ 'overlay-disabled': reorderMode }">
                                            <i class="fas fa-pencil-alt text-white"></i>
                                            <span class="text-white mx-2">
                                                <em>
                                                    {{ $t('judging_program.validated_tricks_box.edit') }}
                                                </em>
                                            </span>
                                        </div>
                                        <div class="col-6 d-flex align-items-center justify-content-end bg-danger bg-opacity-50 rounded-end-pill ms-0 me-0" :class="{ 'overlay-disabled': reorderMode }">
                                            <span class="text-white mx-2"><em> {{ $t('judging_program.validated_tricks_box.delete') }}</em></span><i class="fas fa-trash text-white"></i>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="isValidated" class="row mt-2">
                                    <div class="col-12">
                                        <div class="d-flex flex-column flex-md-row align-items-center justify-content-center gap-2 w-100">
                                            <div class="d-flex align-items-center gap-2">
                                                <span class="text-sm-general">
                                                    <em>{{ $t('judging_program.validated_tricks_box.feedback') }}</em>
                                                </span>
                                                <button
                                                    class="btn btn-sm btn_actions"
                                                    :class="{ 'bg-success text-white': subTrick.athlete_feedback === 1 }"
                                                    @click="updateFeedback(subTrick, 1)"
                                                    title="Confirmado"
                                                >
                                                    <i class="fas fa-thumbs-up"></i>
                                                </button>
                                                <button
                                                    class="btn btn-sm btn_actions"
                                                    :class="{ 'bg-danger text-white': subTrick.athlete_feedback === 2 }"
                                                    @click="updateFeedback(subTrick, 2)"
                                                    title="Incorrecto"
                                                >
                                                    <i class="fas fa-thumbs-down"></i>
                                                </button>
                                            </div>

                                            <div v-if="subTrick.athlete_feedback === 2" class="w-100" style="max-width: 300px;">
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    v-model="subTrick.athlete_comment"
                                                    placeholder="Escribe un comentario..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
            <template v-else>
                <div
                    class="container position-relative"
                    :ref="`draggableContainer-${index}`"
                    @mousedown="startDragging(index)"
                    @mousemove="isView ? null : (isDragging && handleDragging())"
                    @mouseup="isView ? null : stopDragging(index)"
                    @touchstart="isView ? null : startDragging(index)"
                    @touchmove="isView ? null : (isDragging && handleDragging())"
                    @touchend="isView ? null : stopDragging(index)"
                >
                    <div
                        class="row position-relative rounded-pill bg-white"
                        :style="{ transform: `translateX(${index === draggingIndex ? containerOffsetX : 0}px)` }"
                        style="z-index: 2; width: auto;"
                    >
                        <div class="col-1 bg-primary d-flex align-items-center bg-secondary bg-gradient bg-opacity-25  rounded-start-pill ms-0 me-0">
                            <div class="level-circle" :class="getBackgroundColorClass(trick.level)">
                                <b class="text-white text-sm-general">{{ trick.level }}</b>
                            </div>
                        </div>
                      <div class="col-8 position-relative bg-secondary bg-gradient bg-opacity-25  rounded-0 ms-0 me-0">
                            <div class="row">
                                <div class="col-12 text-start p-1 pb-0 mb-0 text-truncate">
                                    <p class="mb-0 text-sm-title d-flex align-items-center gap-1">
                                        <strong>{{ trick.name }}</strong>
                                        <span
                                            v-if="badgeResolver(trick, index)"
                                            class="badge badge-change"
                                            :class="badgeResolver(trick, index).variantClass"
                                            :title="badgeResolver(trick, index).tooltip"
                                            @click.stop="handleBadgeClick(trick, index)"
                                        >
                                            {{ badgeResolver(trick, index).label }}
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 text-start p-1 pt-0 mt-0">
                                    <p class="m-0 p-0 text-secondary text-sm-general">{{ trick.short_name ? trick.short_name : '&nbsp;' }}</p>
                                </div>
                            </div>
                            <div v-if="isStatsExcluded(trick)" class="row">
                                <div class="col-12 text-start p-1 pt-0 mt-0">
                                    <p class="m-0 stats-excluded-note" :title="statsExcludedTooltip(trick)">
                                        <i class="fas fa-triangle-exclamation"></i>
                                        <span>{{ statsExcludedLabel(trick) }}</span>
                                    </p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-6 p-0">
                                    <div class="rounded-pill border border-secondary-subtle text-center" style="background-color: rgb(37,102,199,0.3)">
                                        <p class="m-0 px-1 text-sm-alerts"><em>{{ getCategoryName(trick.category) }}</em></p>
                                    </div>
                                </div>
                                <div class="col-6 p-0">
                                    <div v-if="trick.grab" class="rounded-pill border border-secondary-subtle text-center" style="background-color: rgba(250,230,41,0.3)">
                                        <p class="m-0 px-1 text-sm-alerts" ><em>{{ getGrabName(trick.grab) }}</em></p>
                                    </div>
                                </div>
                            </div>
                        <!-- Icono de feedback del atleta -->
                        <div
                            v-if="isPublishedRound && trick.athlete_feedback"
                            class="position-absolute top-50 end-0 translate-middle-y me-2 d-flex flex-column align-items-end"
                        >
                          <i
                              class="fas"
                              :class="{
      'fa-thumbs-up text-success': trick.athlete_feedback === 1,
      'fa-thumbs-down text-danger': trick.athlete_feedback === 2
    }"
                              style="font-size: 1.2rem;"
                          ></i>

                          <small
                              v-if="isPublishedRound && trick.athlete_feedback === 2 && trick.athlete_comment"
                              class="text-danger text-end mt-1 bg-white bg-opacity-75 px-2 py-1 rounded"
                              style="max-width: 200px;"
                          >
                            <em>{{ trick.athlete_comment }}</em>
                          </small>
                        </div>

                      </div>
                        <div class="col-1 p-0 bg-secondary bg-gradient bg-opacity-25  rounded-0 ms-0 me-0">
                            <div class="flex-column h-100">
                                <div v-if="trick.variation" class="alert alert-secondary m-0 p-0 h-50">
                                    <p class="m-0 text-sm-title">
                                        <strong class="position-absolute top-0 start-50 translate-middle-x" style="line-height: 1.6rem">
                                            {{ trick.variation === 1 ? 'FS' : (trick.variation === 2 ? 'BS' : '') }}
                                        </strong>
                                    </p>
                                </div>
                                <div v-if="trick.dub" class="alert alert-danger m-0 p-0 h-50" style="background-color: rgba(208,0,0,0.7)">
                                    <p class="m-0 text-sm-title">
                                        <strong class="text-white position-absolute top-0 start-50 translate-middle-x">
                                            {{ trick.dub === 1 ? '20%' : (trick.dub === 2 ? '50%' : (trick.dub === 3 ? '80%' : '0%')) }}
                                        </strong>
                                        <i class="fa-solid fa-angle-down text-white position-absolute bottom-0 start-50 translate-middle-x"></i>
                                    </p>
                                </div>

                            </div>
                        </div>
                        <div class="col-2 d-flex justify-content-center align-items-center bg-success bg-gradient bg-opacity-50  rounded-end-pill ms-0 me-0">
                            <div class="text-center text-sm-score">
                                <strong class="p-1">{{ parseFloat(trick.score.toFixed(2)) }}</strong>
                            </div>
                        </div>
                    </div>
                    <!-- Segundo row con botones -->
                    <div class="row position-absolute top-0" style="width: 100%; height: 100%;">
                        <div class="col-6 d-flex align-items-center justify-content-start bg-primary bg-opacity-50 rounded-start-pill ms-0 me-0" :class="{ 'overlay-disabled': reorderMode }">
                            <i class="fas fa-pencil-alt text-white"></i>
                            <span class="text-white mx-2">
                                <em>
                                    {{ $t('judging_program.validated_tricks_box.edit') }}
                                </em>
                        </span>
                        </div>
                        <div class="col-6 d-flex align-items-center justify-content-end bg-danger bg-opacity-50 rounded-end-pill ms-0 me-0" :class="{ 'overlay-disabled': reorderMode }">
                            <span class="text-white mx-2"><em> {{ $t('judging_program.validated_tricks_box.delete') }}</em></span><i class="fas fa-trash text-white"></i>
                        </div>
                    </div>
                </div>
              <!-- Feedback del atleta -->
              <div v-if="isValidated" class="row mt-2 align-items-center">
                <div class="col-auto d-flex align-items-center gap-2">
                    <span class="text-sm-general me-2">
                      <em>{{ $t('judging_program.validated_tricks_box.feedback') }}</em>
                    </span>
                  <button
                      class="btn btn-sm btn_actions"
                      :class="{ 'bg-success text-white': trick.athlete_feedback === 1 }"
                      @click="updateFeedback(trick, 1)"
                      title="Confirmado"
                  >
                    <i class="fas fa-thumbs-up"></i>
                  </button>

                  <button
                      class="btn btn-sm btn_actions"
                      :class="{ 'bg-danger text-white': trick.athlete_feedback === 2 }"
                      @click="updateFeedback(trick, 2)"
                      title="Incorrecto"
                  >
                    <i class="fas fa-thumbs-down"></i>
                  </button>

                </div>

                <!-- Comentario si desaprobado -->
                <div v-if="trick.athlete_feedback === 2" class="col ps-0">
                  <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="trick.athlete_comment"
                      placeholder="Escribe un comentario..."
                  />
                </div>
              </div>

            </template>
        </li>
    </transition-group>
</template>

<script>
import {mapState} from "vuex";
import {isOwnerAthlete, isOwnerJudgeFromCompetition} from "@/helpers/roleUtils";

export default {
    name: "ValidatedBox",
    emits: ['update-validated-tricks','update-trick-validated','delete-trick-validated','update-all-validated-tricks','all-tricks-confirmed','reorder-mode-changed','open-editor-diff'],
    data() {
        return {
            collapsedStates: [],
                        categories : [
                                { id: 1, name: 'Aerial'},
                                { id: 2, name: 'Aerial / Flip'},
                                { id: 3, name: 'Aerial / Switch'},
                                { id: 4, name: 'Dynamic'},
                                { id: 5, name: 'Dynamic / Flip'},
                                { id: 6, name: 'Dynamic / Flip / Switch '},
                                { id: 7, name: 'Dynamic / Switch'},
                                { id: 8, name: 'Dynamic / Transition'},
                                { id: 9, name: 'Mount'},
                                { id: 10, name: 'Static'},
                                { id: 11, name: 'Transition'},
                        ],
                        grabs : [
                                {id: 1, name: 'Blunt', value: '1'},
                                {id: 2, name: 'Boneless', value: '2'},
                                {id: 3, name: 'BuddhaGrab', value: '3'},
                                {id: 4, name: 'CanOpener', value: '4'},
                                {id: 5, name: 'ChristAir', value: '5'},
                                {id: 6, name: 'Crossover', value: '6'},
                                {id: 7, name: 'CrossoverSafety', value: '7'},
                                {id: 8, name: 'CrossoverTruckDriver', value: '8'},
                                {id: 9, name: 'Flat', value: '9'},
                                {id: 10, name: 'HalfSquirrelLef.', value: '10'},
                                {id: 11, name: 'HalfSquirrelRig.', value: '11'},
                                {id: 12, name: 'HighChair', value: '12'},
                                {id: 13, name: 'HorizontalTwist', value: '13'},
                                {id: 14, name: 'Japan', value: '14'},
                                {id: 15, name: 'KneelAir', value: '15'},
                                {id: 16, name: 'Layout', value: '16'},
                                {id: 17, name: 'Mute', value: '17'},
                                {id: 18, name: 'No grab', value: '18'},
                                {id: 19, name: 'Open', value: '19'},
                                {id: 20, name: 'Pike', value: '20'},
                                {id: 21, name: 'SafetyGrab', value: '21'},
                                {id: 22, name: 'Scratch', value: '22'},
                                {id: 23, name: 'Seatbelt', value: '23'},
                                {id: 24, name: 'Shifty', value: '24'},
                                {id: 25, name: 'SpreadEagle', value: '25'},
                                {id: 26, name: 'SquirrelGrab', value: '26'},
                                {id: 27, name: 'Superman', value: '27'},
                                {id: 28, name: 'TruckDriver', value: '28'},
                                {id: 29, name: 'Tuck', value: '29'},
                                {id: 30, name: 'TuckBlunt', value: '30'},
                                {id: 31, name: 'TuckedTwist', value: '31'},
                                {id: 32, name: 'Twist', value: '32'},
                                {id: 33, name: 'Yokohama', value: '33'},
                        ],
                        draggingIndex: null,
                        isDragging: false,
                        startX: 0,
                        containerOffsetX: 0,
                        reorderMode: false,
                        dragSourceIndex: null,
                        athleteFeedbacks: {},

                };
        },
                props: {
                        validatedTricks: {
                                type: Array,
                                default: () => [],
                        },
                        isValidated: {
                                type: Boolean,
                                default: false,
                        },
                        isReadOnly: {
                                type: Boolean,
                                default: false,
                        },
                        diffs: {
                            type: Array,
                            default: () => [],
                        },
                },
        computed:{
            ...mapState("competition",["competitionIdToView","competitionData"]),
            ...mapState("round",["roundAthleteId","roundIdToUpdate","roundIdToView","competitionRounds","roundCompetitionIdToCreate","roundCompetitionIdToView","roundCompetitionIdToEdit"]),
            ...mapState('training', ['trainingRounds','trainingRoundsIdToView']),
            ...mapState('user', ['isAuthenticated','user','token']),
                        diffLookup() {
                            const map = {};
                            const source = Array.isArray(this.diffs) ? this.diffs : [];
                            source.forEach((diff) => {
                                const beforeId = diff?.before?.trick_entry_id || diff?.before?.trick_id;
                                const afterId = diff?.after?.trick_entry_id || diff?.after?.trick_id;

                                const rawIds = [
                                    diff?.trick_entry_id,
                                    diff?.validated_trick_id,
                                    diff?.validated_trick_entry_id,
                                    diff?.trick_id,
                                    diff?.id,
                                    beforeId,
                                    afterId,
                                ].filter((v) => v !== undefined && v !== null);

                                const toInt = (v) => {
                                    const n = Number(v);
                                    return Number.isInteger(n) ? n : null;
                                };
                                const rawIdxs = [diff?.index, diff?.trick_index, diff?.from_index, diff?.index_before, diff?.index_after]
                                    .map((v) => toInt(v))
                                    .filter((v) => Number.isInteger(v));

                                const ids = rawIds.map((v) => `id:${v}`);
                                const idxs = rawIdxs.map((v) => `idx:${v}`);

                                [...rawIds, ...rawIdxs, ...ids, ...idxs].forEach((k) => {
                                    if (!map[k]) map[k] = diff;
                                });
                            });
                            return map;
                        },
            isView(){
                return this.trainingRoundsIdToView || this.roundCompetitionIdToView;
            },
            isPublishedRound() {
                const roundId = this.trainingRoundsIdToView || this.roundCompetitionIdToView || this.roundCompetitionIdToEdit;
                if (!roundId) return false;

                const round = this.competitionRounds.find(r => r.id === roundId);
                if (this.isOwnerAthlete(this.user,round) || this.isOwnerJudgeFromCompetition(this.user,this.competitionData)){
                    return round;
                }

                return round ? round.status_id === 10 : false;
            }
        },
        watch: {
            isReadOnly(newVal) {
                if (newVal && this.reorderMode) {
                    this.reorderMode = false;
                    this.$emit('reorder-mode-changed', false);
                }
            }
        },
        methods: {
            /*ValidatedBox*/
            badgeResolver(trick, idxHint = null) {
                return this.getChangeBadge(trick, idxHint);
            },
            isOwnerAthlete,
            isOwnerJudgeFromCompetition,
            toggleCollapse(combo_id) {
                this.collapsedStates[combo_id] = !this.collapsedStates[combo_id];
            },
            toggleReorderModeFromParent() {
                if (this.isReadOnly) return;
                this.reorderMode = !this.reorderMode;
                this.$emit('reorder-mode-changed', this.reorderMode);
            },
            onDragStart(index, event) {
                if (this.isReadOnly || !this.reorderMode) return;
                this.dragSourceIndex = index;
                if (event && event.dataTransfer) {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', String(index));
                }
            },
            onDragOver(index, event) {
                if (this.isReadOnly || !this.reorderMode) return;
                if (event && event.preventDefault) {
                    event.preventDefault();
                }
            },
            onDrop(index) {
                if (this.isReadOnly || !this.reorderMode) return;
                if (this.dragSourceIndex === null || this.dragSourceIndex === index) {
                    this.dragSourceIndex = null;
                    return;
                }
                const newOrder = [...this.validatedTricks];
                const [item] = newOrder.splice(this.dragSourceIndex, 1);
                newOrder.splice(index, 0, item);
                this.dragSourceIndex = null;
                this.$emit('update-all-validated-tricks', newOrder);
            },
            startDragging(index, subIndex) {
                if (this.reorderMode) return;
                // Sub-tricks inside combos are not draggable/editable
                if (subIndex !== undefined && subIndex !== null) {
                    return;
                }
                this.isDragging = true;
                this.startX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
                this.containerOffsetX = 0;
                this.draggingIndex = subIndex >= 0 ? index + '-' + subIndex : index;
            },
            handleDragging() {
                if (this.reorderMode) return;
                if (this.isDragging) {
                    const currentX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
                    this.containerOffsetX = currentX - this.startX;
                }
            },
            stopDragging(index, subIndex) {
                if (this.reorderMode) return;
                if (this.isDragging) {
                    const threshold = 200;
                    if (Math.abs(this.containerOffsetX) > threshold) {
                        this.containerOffsetX > 0 ? this.editItem(index, subIndex) : this.deleteItem(index, subIndex);
                    }
                    this.isDragging = false;
                    this.startX = 0;
                    this.containerOffsetX = 0;
                    this.draggingIndex = null;
                }
            },
            editItem(index, subIndex) {
                if (subIndex !== undefined && subIndex !== null) {
                    return;
                }
                this.$emit("update-trick-validated", index, subIndex);
            },
            deleteItem(index, subIndex) {
                if (subIndex !== undefined && subIndex !== null) {
                    return;
                }
                this.$emit("delete-trick-validated", index, subIndex);
            },
            getBackgroundColorClass(level) {
                switch (level) {
                    case 1:
                        return 'color-level-1';
                    case 2:
                        return 'color-level-2';
                    case 3:
                        return 'color-level-3';
                    case 4:
                        return 'color-level-4';
                    default:
                        return '';
                }
            },
            getCategoryName(id) {
                const category = this.categories.find((c) => c.id === id);
                return category ? category.name : '';
            },
            getGrabName(id) {
                const grab = this.grabs.find((c) => c.id === id);
                return grab ? grab.name : '';
            },
            isStatsExcluded(trick) {
                return Boolean(trick?.stats_excluded);
            },
            replacementTrickLabel(trick) {
                if (!trick) return '';
                return trick.replacement_trick_name || trick.replacement_trick_short_name || '';
            },
            statsExcludedLabel(trick) {
                const replacement = this.replacementTrickLabel(trick);
                return replacement ? `Error: ${replacement}` : 'Error estadistico';
            },
            statsExcludedTooltip(trick) {
                const replacement = this.replacementTrickLabel(trick);
                return replacement
                    ? `Truco excluido de estadisticas. Correcto: ${replacement}`
                    : 'Truco excluido de estadisticas.';
            },
            comboFeedbackState(trick) {
                if (!this.isPublishedRound) return null;
                if (!trick || !Array.isArray(trick.body)) return null;

                const feedbacks = trick.body
                    .map((item) => Number(item?.athlete_feedback))
                    .filter((value) => value === 1 || value === 2);

                if (!feedbacks.length) return null;
                if (feedbacks.some((value) => value === 2)) return 'bad';
                if (feedbacks.every((value) => value === 1)) return 'ok';
                return null;
            },
                        updateFeedback(trick, feedback) {
                const current = Number(trick.athlete_feedback);
                if (current === feedback) {
                    trick.athlete_feedback = null;
                    trick.athlete_comment = '';
                    delete this.athleteFeedbacks[trick.id];
                } else {
                    trick.athlete_feedback = feedback;
                    if (feedback === 1) {
                        trick.athlete_comment = '';
                    }
                    this.athleteFeedbacks[trick.id] = {
                        athlete_feedback: feedback,
                        athlete_comment: trick.athlete_comment || null,
                    };
                }

                // 🔁 Emitir cada vez que se actualiza un feedback
                this.$emit('update-all-validated-tricks', this.validatedTricks);

                // ✅ Check para saber si todos están confirmados
                this.checkIfAllConfirmed();
            },
                        markAllConfirmed() {
                                const terminalTricks = this.collectTerminalTricks();

                                terminalTricks.forEach((trick) => {
                                        const feedbackValue = Number(trick.athlete_feedback);

                                        // No sobrescribir feedback negativo existente
                                        if (feedbackValue === 2) return;
                                        if (feedbackValue === 1) return; // ya confirmado

                                        trick.athlete_feedback = 1;
                                        trick.athlete_comment = '';
                                        const feedbackKey = this.resolveFeedbackKey(trick);
                                        if (feedbackKey !== null) {
                                                this.athleteFeedbacks[feedbackKey] = {
                                                        athlete_feedback: 1,
                                                        athlete_comment: null,
                                                };
                                        }
                                });

                                this.$emit('update-all-validated-tricks', this.validatedTricks);
                                this.checkIfAllConfirmed(terminalTricks);
                        },

                        checkIfAllConfirmed(flattenedCache = null) {
                                const flatTricks = flattenedCache || this.collectTerminalTricks();
                                const allConfirmed = flatTricks.length > 0 && flatTricks.every((trick) => {
                                        const feedbackValue = Number(trick.athlete_feedback);
                                        return feedbackValue === 1 || feedbackValue === 2;
                                });
                                if (allConfirmed) {
                                        console.log('✅ Todos los tricks fueron confirmados');
                                        this.$emit('all-tricks-confirmed');
                                }
                        },

            collectTerminalTricks() {
                const terminals = [];
                const traverse = (items) => {
                    if (!Array.isArray(items)) {
                        return;
                    }
                    items.forEach((trick) => {
                        const hasComboShape = trick &&
                                typeof trick === 'object' &&
                                Object.prototype.hasOwnProperty.call(trick, 'header') &&
                                Object.prototype.hasOwnProperty.call(trick, 'body');

                        if (hasComboShape && Array.isArray(trick.body)) {
                            traverse(trick.body);
                        } else if (Array.isArray(trick)) {
                            traverse(trick);
                        } else if (trick) {
                            terminals.push(trick);
                        }
                    });
                };

                traverse(this.validatedTricks || []);
                return terminals;
            },

            resolveFeedbackKey(trick) {
                if (!trick) {
                    return null;
                }
                return trick.id || trick.validated_trick_id || trick.trick_id || trick.combo_id || null;
            },
            resolveDiffMeta(trick, idxHint = null) {
                if (!trick || typeof trick !== 'object') return null;

                const resolveKey = (t, idx) => {
                    const idVal = t?.trick_id
                        ?? t?.id
                        ?? t?.trick_entry_id
                        ?? t?.validated_trick_id
                        ?? t?.validated_trick_entry_id
                        ?? null;
                    const idKey = idVal !== null ? `id:${idVal}` : null;

                    const primaryIdx = Number.isInteger(t?.index) ? t.index : null;
                    const secondaryIdx = Number.isInteger(t?.trick_index) ? t.trick_index : null;
                    const hintIdx = Number.isInteger(idx) ? idx : null;
                    const idxVal = hintIdx ?? primaryIdx ?? secondaryIdx;
                    const idxKey = Number.isInteger(idxVal) ? `idx:${idxVal}` : null;

                    return { idKey, idxKey, rawId: idVal, rawIdx: idxVal };
                };

                const { idKey, idxKey } = resolveKey(trick, idxHint);
                const lookup = this.diffLookup;
                const candidates = [
                    trick.__diff,
                    idKey ? lookup[idKey] : null,
                    idxKey ? lookup[idxKey] : null,
                ].filter(Boolean);

                const match = candidates.find((diff) => this.diffMatchesTrick(diff, trick));
                return match || null;
            },
            diffMatchesTrick(diff, trick) {
                if (!diff || !trick) return false;

                const collectIds = (obj) => [
                    obj?.trick_entry_id,
                    obj?.validated_trick_entry_id,
                    obj?.validated_trick_id,
                    obj?.trick_id,
                    obj?.id,
                ].filter((v) => v !== undefined && v !== null);

                const trickIds = collectIds(trick);
                const beforeIds = collectIds(diff.before || {});
                const afterIds = collectIds(diff.after || {});

                const hasIdMatch = trickIds.some((id) => beforeIds.includes(id) || afterIds.includes(id));
                if (hasIdMatch) return true;

                // If diff has no ids but trick has, reject to avoid false positives
                if (trickIds.length && (!beforeIds.length && !afterIds.length)) return false;

                // Fallback: if no id match, allow index match only when action is added/edited and diff carries index
                const action = this.normalizeAction(diff);
                if (action !== 'added' && action !== 'edited') return false;

                const toInt = (v) => {
                    const n = Number(v);
                    return Number.isInteger(n) ? n : null;
                };
                const diffIdx = toInt(diff.index) ?? toInt(diff.trick_index) ?? toInt(diff.from_index) ?? toInt(diff.index_before) ?? toInt(diff.index_after);
                const trickIdx = toInt(trick.index) ?? toInt(trick.trick_index);

                return diffIdx !== null && trickIdx !== null && diffIdx === trickIdx;
            },
            normalizeAction(diffMeta) {
                const raw = (diffMeta?.change_type || diffMeta?.action || '').toLowerCase();
                if (raw === 'removed') return 'removed';
                if (raw === 'moved') return 'moved';
                if (raw === 'added') return 'added';
                return 'edited';
            },
            changeLabel(action) {
                const keyMap = {
                    added: 'judging_program.change_badge_added',
                    edited: 'judging_program.change_badge_edited',
                };
                const key = keyMap[action] || keyMap.edited;
                const txt = this.$t ? this.$t(key) : '';
                if (txt && typeof txt === 'string') return txt;
                if (action === 'added') return 'Nuevo';
                return 'Editado';
            },
            getChangeBadge(trick, idxHint = null) {
                const diffMeta = this.resolveDiffMeta(trick, idxHint);
                if (!diffMeta) return null;

                const action = this.normalizeAction(diffMeta);
                if (action === 'moved' || action === 'removed') return null;

                const label = action === 'added' ? this.changeLabel('added') : this.changeLabel('edited');
                const variantClass = action === 'added' ? 'bg-success text-white' : 'bg-warning text-dark';
                const tooltip = this.buildChangeTooltip(trick, diffMeta);

                return { label, variantClass, tooltip, diff: diffMeta };
            },
            buildChangeTooltip(trick, diffMeta) {
                if (!diffMeta) return '';
                const editorName = diffMeta.editor
                    ? `${diffMeta.editor.name || ''} ${diffMeta.editor.surname || ''}`.trim()
                    : '';
                const parts = [];
                if (editorName) parts.push(editorName);
                if (diffMeta.edited_at) parts.push(this.formatDiffTimestamp(diffMeta.edited_at));
                if (!parts.length) return '';
                return parts.join(' · ');
            },
            formatDiffTimestamp(value) {
                if (!value) return '';
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) return value;

                const pad = (n) => String(n).padStart(2, '0');
                const dd = pad(date.getDate());
                const mm = pad(date.getMonth() + 1);
                const yy = String(date.getFullYear()).slice(-2);
                const hh = pad(date.getHours());
                const mi = pad(date.getMinutes());

                return `${dd}-${mm}-${yy} ${hh}:${mi}`;
            },
            handleBadgeClick(trick, idxHint = null) {
                const diffMeta = this.resolveDiffMeta(trick, idxHint);
                if (!diffMeta) return;

                const editorId = this.getEditorIdFromDiff(diffMeta);
                if (!editorId) return;

                this.$emit('open-editor-diff', { editorId, diff: diffMeta });
            },
            getEditorIdFromDiff(diffMeta) {
                const editor = diffMeta?.editor || {};
                return editor.judge_id
                    || editor.id
                    || editor.user_id
                    || diffMeta?.editor_id
                    || null;
            },
    }
};
</script>

<style scoped>
/*ValidatedBox*/
.list-tricks {
    list-style-type: none;
    padding: 0;
    max-height: 500px;
    overflow-y: auto;
    overflow-x: hidden;
}
.list-tricks li {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #ccc;
    padding: 8px;
}
.with-borders {
    border-top: 1px solid #000000;
    border-bottom: 1px solid #000000;
    border-right: 2px solid #000000;
    border-left: 2px solid #000000;
    text-align: center;
}
.color-level-1 {
    color:#ffffff;
    background-color: rgb(8,76,255);
}
.color-level-2 {
    color:#ffffff;
    background-color: rgb(23,231,0);
}
.color-level-3 {
    color:#ffffff;
    background-color: rgb(255,126,0);
}
.color-level-4 {
    color:#ffffff;
    background-color: rgb(255,0,0);
}
.level-circle-combo {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1px solid #000;
    color: #000;
    background-color: #fff;
}
.level-circle {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1px solid #000;
    line-height: 7px;
}
.text-sm-general{
    font-size: 0.6rem;
    line-height: 10px;
}
.text-sm-alerts{
    font-size: 0.6rem;
}
.text-sm-score{
    font-size: 0.6rem;
    width: 17vw;
}
.text-sm-title{
    font-size: 0.7rem;
}

.reorder-active {
    cursor: grab;
}

.overlay-disabled {
    pointer-events: none;
}

.combo-body-group {
    background: rgba(37, 102, 199, 0.08);
    border: 1px solid rgba(37, 102, 199, 0.2);
    border-radius: 14px;
    padding: 8px;
    margin-top: 6px;
    width: 100%;
    align-self: stretch;
}

.combo-body {
    border-radius: 12px;
    padding: 2px 4px;
}

.combo-body-group > .container,
.combo-body .container {
    max-width: 100%;
}

.combo-pill {
    padding: 2px 6px;
    line-height: 1.1;
}

.combo-pill p {
    font-size: 0.55rem;
}

.combo-feedback-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    color: inherit;
}

.combo-feedback-icon--ok {
    color: #198754;
}

.combo-feedback-icon--bad {
    color: #dc3545;
}

.stats-excluded-note {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: #b54708;
    font-size: 0.55rem;
    line-height: 1.1;
    font-weight: 600;
}

.stats-excluded-note i {
    flex-shrink: 0;
}

@media (min-width: 450px) {
    .text-sm-general{
        font-size: 1rem;
        line-height: 25px;
    }
    .text-sm-alerts{
        font-size: 0.9rem;
    }
    .text-sm-score{
        font-size: 1rem;
        width: 10vw;
    }
    .text-sm-title{
        font-size: 1rem;
    }
    .combo-pill {
        padding: 3px 8px;
    }
    .combo-pill p {
        font-size: 0.75rem;
    }
    .badge-change {
        font-size: 0.65rem;
        cursor: pointer;
    }
    .stats-excluded-note {
        font-size: 0.72rem;
    }
    .level-circle {
        width: 30px;
        height: 30px;
        line-height: 25px;

    }
    .level-circle-combo {
        width: 30px;
        height: 30px;
    }

}
</style>